import { HasIdAndName } from "@homebound/beam";
import { ReadyPlanType, SaveReadyPlanLocationInput } from "src/generated/graphqlTypes";
import {
  PlanPackageLocationsV2Query,
  PlanPackageLocationsV2_LocationFragment,
} from "src/routes/libraries/plan-package-v2/components/LocationsTab.generated";
import { fail } from "src/utils";

export type RoomFormValue = Pick<SaveReadyPlanLocationInput, "id" | "roomTypeId" | "marketingName" | "roomNumber">;
export type SaveRpls = (inputs: SaveReadyPlanLocationInput[], opts?: { membershipChanged?: boolean }) => Promise<void>;
/** A level and its rooms as rendered on the Locations tab (UI grouping) */
export type LevelGroup = {
  /** The level Location this group renders under. */
  location: HasIdAndName;
  /** The level's own RPL, when one exists (legacy levels may only be derivable from their rooms). */
  levelRpl?: PlanPackageLocationsV2_LocationFragment;
  /** Room RPLs sorted by full room number. */
  rooms: PlanPackageLocationsV2_LocationFragment[];
};

/** Groups RPLs into Level + rooms groups, keeping roomless levels (e.g. Roof), sorted for display. */
export function groupRoomLocations(rpls: PlanPackageLocationsV2_LocationFragment[]): LevelGroup[] {
  // Level RPLs point at their level via `location`, room RPLs via `level`
  const levels = [...rpls.map((rpl) => rpl.level), ...rpls.map((rpl) => rpl.location)].compact().uniqueByKey("id");
  return rpls
    .groupByObject((rpl) => rpl.level?.id ?? rpl.location.id)
    .map(([levelId, group]) => ({
      location: levels.find((l) => l.id === levelId) ?? fail(`Could not find level for ${group[0].id}`),
      levelRpl: group.find((rpl) => !rpl.level),
      rooms: group.filter((rpl) => !!rpl.roomNumber).sortBy((rpl) => rpl.fullRoomNumber ?? 0),
    }))
    .sortBy((group) => levelSortKey(group.location.name));
}

/** Sorts Basement -> Level 1..N -> ADU -> Roof -> anything else (the old step relied on luck-based alphasort). */
export function levelSortKey(levelName: string): string {
  if (/basement$/i.test(levelName)) return "0";
  const levelNumber = levelName.match(/\d+$/)?.first;
  if (levelNumber) return `1-${levelNumber.padStart(3, "0")}`;
  if (isAduLevel(levelName)) return "2";
  if (/roof$/i.test(levelName)) return "3";
  return `4-${levelName}`;
}

/** Rooms are numbered 1-99 within each level prefix. */
const minRoomNumber = 1;
export const maxRoomNumber = 99;

/**
 * The prefix of a level's full room numbers (Level 2 rooms are 2xx): Basement rooms are 1-99,
 * ADU rooms share Level 1's 1xx range, and Roof (undefined) cannot have rooms.
 */
export function roomNumberPrefix(levelName: string): number | undefined {
  if (/basement$/i.test(levelName)) return 0;
  if (isAduLevel(levelName)) return 1;
  if (/roof$/i.test(levelName)) return undefined;
  const levelNumber = levelName.match(/\d+$/)?.first;
  return levelNumber ? Number(levelNumber) : undefined;
}

/** Formats a room's display number, e.g. (Level 2, 5) -> 205. */
export function formatFullRoomNumber(levelName: string, roomNumber: number): number | undefined {
  const prefix = roomNumberPrefix(levelName);
  return prefix === undefined ? undefined : prefix * 100 + roomNumber;
}

/** Validates a typed full room number against its level's range, returning the 2-digit part to save. */
export function validateFullRoomNumber(
  levelName: string,
  fullRoomNumber: number,
): { roomNumber: number } | { error: string } {
  const prefix = roomNumberPrefix(levelName);
  if (prefix === undefined) return { error: `${levelName} cannot have rooms` };
  const [min, max] = [prefix * 100 + minRoomNumber, prefix * 100 + maxRoomNumber];
  if (fullRoomNumber < min || fullRoomNumber > max) {
    return { error: `${levelName} room numbers must be between ${min} and ${max}` };
  }
  return { roomNumber: fullRoomNumber - prefix * 100 };
}

/** The next free room number when adding a room to `levelName`, computed across all levels sharing its prefix. */
export function nextRoomNumber(levelName: string, groups: LevelGroup[]): number {
  const prefix = roomNumberPrefix(levelName);
  const taken = groups
    .filter((group) => roomNumberPrefix(group.location.name) === prefix)
    .flatMap((group) => group.rooms.map((rpl) => rpl.roomNumber!));
  return taken.isEmpty ? minRoomNumber : taken.max() + 1;
}

/**
 * Reassigns the level's existing room numbers (as a set, old-step semantics: given rooms 101/103/107,
 * a reorder still uses 101/103/107) to the new visual order, returning only the rows that changed.
 */
export function permuteRoomNumbers(
  rooms: { id: string; roomNumber: number }[],
  newOrderIds: string[],
): { id: string; roomNumber: number }[] {
  if (rooms.length !== newOrderIds.length) fail("Expected rooms and newOrderIds to be the same length");
  const sortedNumbers = rooms.map((room) => room.roomNumber).sortBy((n) => n);
  return newOrderIds
    .map((id, ix) => ({ id, roomNumber: sortedNumbers[ix] }))
    .filter(({ id, roomNumber }) => rooms.find((room) => room.id === id)!.roomNumber !== roomNumber);
}

/**
 * The new levels offerable in the Add modal: only the next sequential "Level N" (Level 3 can't be
 * added before Level 2), plus non-numeric levels (Basement/Roof), excluding levels already in use.
 * ADUs are their own plan packages, so ADU plan packages are only offered ADU levels and all others never are.
 */
export function usableNewLevels(
  queryLevels: PlanPackageLocationsV2Query["levels"],
  groups: LevelGroup[],
  planType: ReadyPlanType,
): HasIdAndName[] {
  const isAduPlan = planType === ReadyPlanType.Adu;
  const planLevels = queryLevels.filter((l) => l.parent && isAduLevel(l.name) === isAduPlan);
  const numberedLevelsInUse = groups.filter((group) => isNumberedLevel(group.location.name));
  const [numberedLevels, otherLevels] = planLevels.partition((l) => isNumberedLevel(l.name));
  return numberedLevels
    .sortByKey("name")
    .slice(0, numberedLevelsInUse.length + 1)
    .concat(otherLevels)
    .filter((l) => !groups.some((group) => group.location.id === l.id))
    .sortBy((l) => levelSortKey(l.name));
}

export function isNumberedLevel(levelName: string): boolean {
  return /Level \d+/i.test(levelName);
}

function isAduLevel(levelName: string): boolean {
  return /adu$/i.test(levelName);
}

export function activeOptionsCount(rpl: PlanPackageLocationsV2_LocationFragment): number {
  return rpl.readyPlanOptions.filter((rpo) => rpo.active).length;
}

/** Describes the rooms affected by a renumber, e.g. "Level 1 Rooms 101 - 105" or "Level 1 Room 101". */
export function describeRoomRange(levelName: string, fullRoomNumbers: number[]): string {
  const [min, max] = [fullRoomNumbers.min(), fullRoomNumbers.max()];
  return min === max ? `${levelName} Room ${min}` : `${levelName} Rooms ${min} - ${max}`;
}
